import {
  proStudioUrqlGql as urqlGql,
  SolutionEnvironmentsEnum,
} from '@pypestream/api-services';

import { checkForErrors } from './check-for-errors';

/** A pype (per env) bound to a connection — one `pype_connection_pype` row. */
export interface ConnectionPype {
  pypeId: string;
  env: SolutionEnvironmentsEnum;
}

/**
 * A Live Agent Platform connection == a `PypeConnection` row. Unlike a rule set
 * it IS env-scoped, via its own `environment` — the settings page's
 * Sandbox/Live toggle filters on it.
 *
 * The connection owns the routing values; the backend copies them onto every
 * pype in `pypes` (see backend#1262). Nothing here writes a pype.
 */
export interface PlatformConnection {
  id: string;
  name: string;
  /**
   * Identifies the actual platform for the logo and the modal's dropdown:
   * the integration name for external integrations (`Quiq`, `Genesys`), the
   * `agent_routing` value for internal ones (`salesforce`, `cc`). Same key
   * space as `resolvePlatformKey`.
   */
  platformId: string;
  environment: SolutionEnvironmentsEnum;
  /** The whole `agent_routing_config` the backend writes onto bound pypes. */
  settings: Record<string, unknown> | null;
  agentRouting: string;
  agentRoutingUrl?: string;
  /** Pypes/channels bound to this connection — the source of truth for binding. */
  pypes: ConnectionPype[];
  createdAt?: string;
  updatedAt?: string;
}

/** `settings` arrives as the `JSON` scalar (`any`); keep only object shapes. */
const toSettings = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Loads every Live Agent Platform connection for a project. The operations live
 * on the `pypestream-admin-backend` remote schema, which Hasura namespaces under
 * `admin_`, so the payload is unwrapped here and normalized into the flat
 * `PlatformConnection` shape the rest of the app consumes.
 */
export const getPypeConnections = async ({
  projectId,
}: {
  projectId: string;
}): Promise<PlatformConnection[]> => {
  const response = await urqlGql.getPypeConnections({ projectId });

  checkForErrors(response);

  const connections = response.data?.admin_?.pypeConnectionsByProjectId ?? [];

  return connections.filter(Boolean).map((connection) => ({
    id: connection?.id ?? '',
    name: connection?.name ?? '',
    platformId: connection?.platformId ?? '',
    // Match the column's own default (`PypeEnvironment ... DEFAULT 'Live'`). The
    // column is NOT NULL so this shouldn't fire, but defaulting the other way
    // would file a Live connection under the Sandbox tab.
    environment: connection?.environment ?? SolutionEnvironmentsEnum.Live,
    settings: toSettings(connection?.settings),
    agentRouting: connection?.agentRouting ?? '',
    agentRoutingUrl: connection?.agentRoutingUrl ?? undefined,
    pypes: (connection?.pypes ?? [])
      .filter(Boolean)
      .filter((pype) => pype?.pypeId && pype?.env)
      .map((pype) => ({
        pypeId: pype?.pypeId ?? '',
        env: pype?.env as SolutionEnvironmentsEnum,
      })),
    createdAt: connection?.createdAt ?? undefined,
    updatedAt: connection?.updatedAt ?? undefined,
  }));
};
